import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { replaceRelations, searchProducts } from '../../api/business/endpoints';
import type { ProductRelationKind, ProductSummary, RelatedProduct } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { movedInList, relatedIdsOf, withoutInList } from '../../components/relations';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useLanguage } from '../../i18n/LanguageContext';

type RelatedProductsListProps = {
  companyId: string;
  productId: string;
  kind: ProductRelationKind;
  related: Array<RelatedProduct>;
  canManage: boolean;
};

/**
 * One kind of a product's related products (R0030 WP-08): the list in its order, each with its main photo and an
 * archived one marked; to whoever may manage products, a picker that searches the company's products by name, SKU
 * or EAN, arrows to reorder and a button to remove. Every change saves the whole list at once.
 */
export const RelatedProductsList = ({
  companyId,
  productId,
  kind,
  related,
  canManage,
}: RelatedProductsListProps) => {
  const { t } = useLanguage();
  const [term, setTerm] = useState('');
  const [reciprocal, setReciprocal] = useState(false);
  const debounced = useDebouncedValue(term.trim(), 300);
  const ids = relatedIdsOf(related);
  const candidates = useQuery({
    queryKey: ['relation-candidates', companyId, debounced],
    queryFn: () =>
      searchProducts(companyId, {
        term: debounced,
        status: 'ACTIVE',
        page: 1,
        sort: 'name',
        direction: 'asc',
      }),
    enabled: canManage,
  });
  const save = useAppMutation(
    ({ productIds, both }: { productIds: Array<string>; both: boolean }) =>
      replaceRelations(companyId, productId, kind, { productIds, reciprocal: both }),
    ['product', 'products'],
  );
  const options = (candidates.data?.items ?? []).filter(
    (option) => option.id !== productId && !ids.includes(option.id),
  );

  return (
    <Box>
      <ErrorNotice error={save.error} />
      {related.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {t('relations.empty')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {related.map((one, index) => (
            <ListItem
              key={one.id}
              disableGutters
              secondaryAction={
                canManage ? (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('relations.moveUp')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('relations.moveUp')}
                          disabled={index === 0 || save.isPending}
                          onClick={() =>
                            save.mutate({ productIds: movedInList(ids, index, -1), both: false })
                          }
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('relations.moveDown')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('relations.moveDown')}
                          disabled={index === related.length - 1 || save.isPending}
                          onClick={() => save.mutate({ productIds: movedInList(ids, index, 1), both: false })}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('relations.remove')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('relations.remove')}
                          disabled={save.isPending}
                          onClick={() => save.mutate({ productIds: withoutInList(ids, one.id), both: false })}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : undefined
              }
            >
              <ListItemAvatar>
                <Avatar variant="rounded" src={one.mainImage?.urls.thumb.webp} alt={one.name}>
                  {one.name.slice(0, 1)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{one.name}</span>
                    {one.status === 'ARCHIVED' ? (
                      <Chip size="small" color="warning" variant="outlined" label={t('relations.archived')} />
                    ) : null}
                  </Stack>
                }
                secondary={one.sku ?? undefined}
              />
            </ListItem>
          ))}
        </List>
      )}
      {canManage ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          sx={{ mt: 1 }}
        >
          <Autocomplete<ProductSummary>
            sx={{ flex: 1 }}
            size="small"
            options={options}
            value={null}
            loading={candidates.isFetching}
            disabled={save.isPending}
            filterOptions={(all) => all}
            getOptionLabel={(option) => [option.name, option.sku, option.ean].filter(Boolean).join(' · ')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Avatar
                  variant="rounded"
                  src={option.mainImage?.urls.thumb.webp}
                  alt={option.name}
                  sx={{ width: 28, height: 28, mr: 1.5 }}
                >
                  {option.name.slice(0, 1)}
                </Avatar>
                {[option.name, option.sku, option.ean].filter(Boolean).join(' · ')}
              </li>
            )}
            onChange={(_event, value) => {
              if (value !== null) {
                save.mutate({ productIds: [...ids, value.id], both: kind === 'CROSS_SELL' && reciprocal });
                setTerm('');
              }
            }}
            inputValue={term}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input') setTerm(value);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t('relations.add')} placeholder={t('relations.searchHint')} />
            )}
          />
          {kind === 'CROSS_SELL' ? (
            <FormControlLabel
              control={<Checkbox checked={reciprocal} onChange={(e) => setReciprocal(e.target.checked)} />}
              label={t('relations.reciprocal')}
            />
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};
